import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { RideStore } from "../ride/store";

/**
 * A chase camera that trails the local rider. It uses exponential damping so a
 * snapshot correction never produces a camera snap.
 */
export function CameraRig({ store }: { store: RideStore }) {
  const lookTarget = useRef(new THREE.Vector3());
  const desired = useRef(new THREE.Vector3());
  const forward = useRef(new THREE.Vector3());
  const target = useRef(new THREE.Vector3());
  const initialised = useRef(false);
  const scratch = useMemo(() => new THREE.Vector3(), []);

  useFrame(({ camera }, delta) => {
    const now = Date.now();
    const pose = store.getSelfPose(now);
    if (!pose) return;

    const dt = Math.min(delta, 0.1);
    const speed = store.getSelfView(now)?.speed ?? 0;
    forward.current.set(Math.sin(pose.heading), 0, Math.cos(pose.heading));
    target.current.set(pose.x, pose.y + 1.05, pose.z);

    const trail = 5.2 + Math.min(1.4, speed * 0.06);
    desired.current
      .copy(target.current)
      .addScaledVector(forward.current, -trail)
      .add(scratch.set(0, 2.2, 0));

    if (!initialised.current) {
      camera.position.copy(desired.current);
      lookTarget.current
        .copy(target.current)
        .addScaledVector(forward.current, 5)
        .add(scratch.set(0, 0.15, 0));
      initialised.current = true;
    } else {
      const positionAlpha = 1 - Math.exp(-dt * 4.4);
      const lookAlpha = 1 - Math.exp(-dt * 8.5);
      if (camera.position.distanceTo(desired.current) > 80) {
        camera.position.copy(desired.current);
        lookTarget.current.copy(target.current);
      } else {
        camera.position.lerp(desired.current, positionAlpha);
        lookTarget.current.lerp(
          scratch.copy(target.current).addScaledVector(forward.current, 5),
          lookAlpha,
        );
      }
    }

    camera.lookAt(lookTarget.current);
  });

  return null;
}
